import { useCallback, useEffect, useState } from 'react'
import type { Challenge, ChallengeWeek } from '@/domain/workout'
import type { Progress, SessionRecord } from '@/domain/session'
import { bestRecordOfWeek, repsPerRound, scoredPhase } from '@/domain/session'
import { cx } from '@/lib/cx'
import { audioCues } from '@/services/audioCues'
import { type AwakeMode, screenAwake } from '@/services/screenAwake'
import { selectIsLastPhase, selectPhase, useSessionStore } from '@/store/sessionStore'
import { AfapView } from './AfapView'
import { AmrapView } from './AmrapView'
import { BriefingView } from './BriefingView'
import { CountdownView } from './CountdownView'
import { IntervalView } from './IntervalView'
import { SummaryView } from './SummaryView'
import { usePhaseRunner } from './usePhaseRunner'
import styles from './workout.module.css'

interface WorkoutOverlayProps {
  challenge: Challenge
  week: ChallengeWeek
  progress: Progress
  onSave: (session: Omit<SessionRecord, 'id'>) => Promise<void>
  onClose: () => void
}

/** La pantalla del entreno: ocupa todo y no deja nada más a la vista. */
export function WorkoutOverlay({ challenge, week, progress, onSave, onClose }: WorkoutOverlayProps) {
  const phase = useSessionStore(selectPhase)
  const phaseIndex = useSessionStore((state) => state.phaseIndex)
  const stage = useSessionStore((state) => state.stage)
  const clock = useSessionStore((state) => state.clock)
  const summary = useSessionStore((state) => state.summary)
  const rounds = useSessionStore((state) => state.rounds)
  const afapSeconds = useSessionStore((state) => state.afapSeconds)
  const isLastPhase = useSessionStore(selectIsLastPhase)

  const start = useSessionStore((state) => state.start)
  const pause = useSessionStore((state) => state.pause)
  const nextPhase = useSessionStore((state) => state.nextPhase)
  const previousPhase = useSessionStore((state) => state.previousPhase)
  const restartPhase = useSessionStore((state) => state.restartPhase)

  const runtime = usePhaseRunner()
  const [awake, setAwake] = useState<AwakeMode>('none')

  const running = clock.status === 'running'

  /* ── Pantalla encendida ─────────────────────────────────────────────── */
  useEffect(() => {
    if (running && stage === 'work') {
      void screenAwake.enable().then(setAwake)
    } else {
      void screenAwake.disable()
      setAwake('none')
    }
  }, [running, stage])

  useEffect(() => () => void screenAwake.disable(), [])

  /* ── Mandos ─────────────────────────────────────────────────────────── */
  const handlePrimary = useCallback(() => {
    audioCues.unlock()

    if (stage === 'done') {
      nextPhase()
      return
    }
    if (running) {
      pause()
      return
    }
    audioCues.play('start')
    start()
  }, [stage, running, nextPhase, pause, start])

  const handleSecondary = useCallback(() => {
    if (stage === 'briefing') {
      if (phaseIndex > 0) previousPhase()
      else onClose()
      return
    }
    restartPhase()
  }, [stage, phaseIndex, previousPhase, restartPhase, onClose])

  /* Barra espaciadora: pausar y seguir sin mirar la pantalla. */
  useEffect(() => {
    if (summary) return
    const onKey = (event: KeyboardEvent) => {
      if (event.code !== 'Space') return
      const target = event.target as HTMLElement | null
      if (target && ['INPUT', 'TEXTAREA', 'BUTTON'].includes(target.tagName)) return
      event.preventDefault()
      handlePrimary()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [handlePrimary, summary])

  /* ── Datos para el cierre ───────────────────────────────────────────── */
  const scored = scoredPhase(week)
  const countedRounds = scored ? (rounds[scored.id] ?? 0) : 0
  const finisher = week.workout.phases.find((item) => item.kind === 'afap')
  const finisherSeconds = finisher ? (afapSeconds[finisher.id] ?? null) : null

  const benchmark =
    week.benchmarkOf && scored
      ? bestRecordOfWeek(progress, week.benchmarkOf, repsPerRound(scored))
      : null

  const primaryLabel = (() => {
    if (stage === 'done') return isLastPhase ? 'Terminar' : 'Siguiente'
    if (stage === 'briefing') return phase?.kind === 'afap' ? 'Empezar AFAP' : 'Empezar bloque'
    if (running) return 'Pausa'
    return clock.accumulatedMs > 0 ? 'Seguir' : 'Empezar'
  })()

  return (
    <div className={styles.overlay}>
      <div className={styles.bar}>
        <button type="button" className={styles.exit} onClick={onClose}>
          {/* En el cierre se avisa: salir aquí es tirar la sesión sin apuntarla. */}
          {summary ? '← Salir sin guardar' : '← Salir'}
        </button>
        <span className={styles.tag}>
          {summary ? `Semana ${String(week.number).padStart(2, '0')}` : phase?.tag}
        </span>
      </div>

      {summary ? (
        <SummaryView
          challenge={challenge}
          week={week}
          progress={progress}
          countedRounds={countedRounds}
          afapSeconds={finisherSeconds}
          onSave={onSave}
          onClose={onClose}
        />
      ) : (
        <>
          <div className={styles.stage}>
            {phase && stage === 'briefing' && <BriefingView phase={phase} />}
            {phase && stage !== 'briefing' && (
              <>
                {phase.kind === 'intervals' && <IntervalView phase={phase} runtime={runtime} />}
                {phase.kind === 'amrap' && (
                  <AmrapView phase={phase} runtime={runtime} benchmark={phase.scored ? benchmark : null} />
                )}
                {phase.kind === 'afap' && <AfapView phase={phase} runtime={runtime} />}
                {phase.kind === 'countdown' && <CountdownView phase={phase} runtime={runtime} />}
              </>
            )}
          </div>

          {running && awake === 'none' && (
            <p className={styles.awakeWarning}>
              Este móvil no deja mantener la pantalla encendida: súbele el tiempo de bloqueo
            </p>
          )}

          <div className={styles.controls}>
            <button type="button" className={styles.control} onClick={handleSecondary}>
              {stage === 'briefing' ? (phaseIndex > 0 ? 'Atrás' : 'Salir') : 'Reiniciar'}
            </button>
            <button
              type="button"
              className={cx(styles.control, styles.controlPrimary)}
              onClick={handlePrimary}
            >
              {primaryLabel}
            </button>
          </div>
        </>
      )}
    </div>
  )
}
